import type { Ref } from "vue";

import type { CoursePageView } from "~/business-areas/course/course.model";
import type { CourseRatingItemView } from "~~/model/course.model";

export function useCoursePageMeta({
  course,
  ratingsWithComment,
}: {
  course: Ref<CoursePageView>;
  ratingsWithComment: Ref<CourseRatingItemView[]>;
}) {
  const {
    public: {
      apps: { websiteBaseUrl },
    },
  } = useRuntimeConfig();

  const title = computed(
    () =>
      `Formation ${course.value?.subject || course.value?.title} | Certifié Qualiopi`,
  );

  useSeoMeta({
    title,
    description: () => course.value?.description,
    ogImage: () =>
      course.value?.og_image_url ??
      `${websiteBaseUrl}/images/brand/og-image.jpg`,
    ogTitle: title,
    ogImageAlt: title,
    twitterCard: "summary_large_image",
    twitterTitle: title,
    twitterDescription: () =>
      course.value?.teachers[0]
        ? `${course.value?.teachers[0].full_name}, ${course.value?.teachers[0].job}`
        : undefined,
    twitterImage: () => course.value?.og_image_url,
    robots: course.value?.indexed ? undefined : "noindex",
  });

  useSchemaOrg([
    defineCourse(
      computed(() => {
        return {
          "@id": `${websiteBaseUrl}/courses/${course.value?.slug}#schema`,
          "@type": "Course",
          name: course.value?.title,
          provider: {
            "@id": `${websiteBaseUrl}/#identity`,
          },
          description: course.value?.description,
          image: course.value?.og_image_url ?? undefined,
          author: course.value?.teachers[0]
            ? definePerson({
                name: course.value?.teachers[0].full_name ?? "",
                image: course.value?.teachers[0].avatar_url,
                url: `${websiteBaseUrl}/trainers/${course.value?.teachers[0].slug}`,
              })
            : undefined,
          ...(course.value?.ratings
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: course.value.ratings.avg,
                  reviewCount: course.value.ratings.count,
                },
              }
            : {}),
          review: ratingsWithComment.value.map((rating) => {
            return {
              "@type": "Review",
              author: {
                "@type": "Person",
                name: rating.author.full_name,
              },
              datePublished: rating.created_at,
              reviewBody: rating.comment,
              reviewRating: {
                "@type": "Rating",
                bestRating: "5",
                ratingValue: rating.global_satisfaction,
                worstRating: "1",
              },
            };
          }),
        };
      }),
    ),
    ...(course.value?.video_url
      ? [
          defineVideo({
            name: title,
            description: course.value.description,
            contentUrl: course.value.video_url,
            thumbnailUrl: course.value.og_image_url,
            width: 1920,
            height: 1080,
            inLanguage: course.value.languages[0],
            uploadDate: course.value.created_at ?? undefined,
          }),
        ]
      : []),
  ]);
}
